import React, { useState, useEffect, useMemo } from "react";

/**
 * WeekAhead.jsx — RR Week Ahead
 * ==============================
 * Layout Bloomberg-style: colunas horizontais por dia (rolam lateralmente
 * quando faltam pixels), cada coluna com um HERO CARD que roda entre os
 * eventos do dia, e uma lista compacta agrupada macro/earnings embaixo.
 *
 * A ideia é não empilhar 15 cards verticalmente. O olho vai pra um só,
 * o resto vira índice clicável.
 *
 * Fontes de imagem, em ordem:
 *   1. week_ahead_images/{event_id}.webp — gerada pelo Klein
 *   2. logo do ticker                    — /logos/{TICKER}.svg
 *   3. bandeira do país                  — fallback pra macro
 *
 * Quando há foto (CEO/executivo), o logo vira par do título: mesma linha,
 * sem fundo branco, direto sobre o card. Sem foto, o logo ocupa o slot
 * da imagem inteira.
 */

const C = {
  purple:     "#6660fe",
  purpleDim:  "rgba(102, 96, 254, 0.14)",
  purpleEdge: "rgba(102, 96, 254, 0.34)",
  bg:         "#0e0d1f",
  card:       "rgba(18, 18, 18, 0.82)",
  border:     "rgba(255, 255, 255, 0.08)",
  text:       "#f4f4f6",
  muted:      "#8b8a99",
  dim:        "#5f5e6e",
  gold:       "#c9a227",
  green:      "#22c55e",
  red:        "#DC143C",
};

const IMPACT = {
  High:    { label: "ALTO",    color: C.red,   bg: "rgba(220,20,60,.13)" },
  Medium:  { label: "MÉDIO",   color: C.gold,  bg: "rgba(201,162,39,.13)" },
  Low:     { label: "BAIXO",   color: C.muted, bg: "rgba(139,138,153,.10)" },
  Holiday: { label: "FERIADO", color: C.dim,   bg: "rgba(95,94,110,.10)" },
};

const ANGLE = {
  otimista:     { label: "otimista",     color: C.green },
  cauteloso:    { label: "cauteloso",    color: C.red },
  neutro:       { label: "neutro",       color: C.muted },
  disputado:    { label: "disputado",    color: C.gold },
  sem_consenso: { label: "sem consenso", color: C.dim },
};

const SLOT_PT = {
  "Pre Market":    "PRÉ",
  "During Market": "PREGÃO",
  "Post Market":   "PÓS",
};

const ROTATE_MS = 5000;

/* ── Utilidades ─────────────────────────────────────────────────────────── */

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpoint]);
  return mobile;
}

function isToday(dateStr) {
  const br = new Date(Date.now() - 3 * 3600 * 1000);
  return br.toISOString().slice(0, 10) === dateStr;
}

function tickerBase(t) {
  return (t || "").split(".")[0].toUpperCase();
}

function shortName(card) {
  if (card.kind === "earnings" && card.company) return card.company;
  return (card.title || "").split("#")[0].trim();
}

/* ── Thumbnail ──────────────────────────────────────────────────────────── */

function Thumb({ card, imgUrl, logoUrl, showImg, showLogo, onImgFail, onLogoFail }) {
  return (
    <div style={{
      position: "relative",
      width: "100%",
      aspectRatio: "16 / 10",
      overflow: "hidden",
      background: showImg
        ? "#000"
        : `linear-gradient(145deg, ${C.purpleDim}, rgba(14,13,31,.9))`,
      borderBottom: `1px solid ${C.border}`,
    }}>
      {showImg && (
        <img
          src={imgUrl}
          alt=""
          loading="lazy"
          onError={onImgFail}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      )}

      {/* Sem imagem: logo grande centralizado, ou bandeira */}
      {!showImg && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {showLogo ? (
            <img
              src={logoUrl}
              alt=""
              onError={onLogoFail}
              style={{ width: "44%", maxHeight: "58%", objectFit: "contain", opacity: 0.92 }}
            />
          ) : (
            <span style={{ fontSize: 40, opacity: 0.5 }}>{card.flag || "◆"}</span>
          )}
        </div>
      )}

      {/* Bandeira no canto superior direito */}
      {card.flag && (
        <span style={{
          position: "absolute", top: 8, right: 9, fontSize: 15,
          filter: "drop-shadow(0 1px 3px rgba(0,0,0,.6))",
        }}>
          {card.flag}
        </span>
      )}
    </div>
  );
}

/* ── Sub-componentes ────────────────────────────────────────────────────── */

function ImpactBadge({ impact }) {
  const cfg = IMPACT[impact] || IMPACT.Low;
  return (
    <span style={{
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 9.5, fontWeight: 600, letterSpacing: ".06em",
      color: cfg.color, background: cfg.bg,
      border: `1px solid ${cfg.color}33`,
      padding: "1.5px 6px", borderRadius: 3, whiteSpace: "nowrap",
    }}>
      {cfg.label}
    </span>
  );
}

function DataStrip({ card }) {
  const d = card.data || {};
  const items = [];
  if (d.eps_estimate != null) items.push(["EPS est", Number(d.eps_estimate).toFixed(2)]);
  if (d.forecast) items.push(["prev", d.forecast]);
  if (d.previous) items.push(["ant", d.previous]);
  if (!items.length) return null;
  return (
    <div style={{ display: "flex", gap: 11, flexWrap: "wrap", marginTop: 7 }}>
      {items.map(([k, v]) => (
        <span key={k} style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace",
                         fontSize: 9, color: C.dim, letterSpacing: ".04em" }}>{k}</span>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace",
                         fontSize: 12, fontWeight: 500, color: C.text }}>{v}</span>
        </span>
      ))}
    </div>
  );
}

function HeroCard({ card, imgBase, logoBase }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const imgUrl = imgBase ? `${imgBase}/${card.event_id}.webp` : null;
  const base = tickerBase(card.ticker);
  const logoUrl = base && logoBase ? `${logoBase}/logos/${base}.svg` : null;

  const showImg  = Boolean(imgUrl)  && !imgFailed;
  const showLogo = Boolean(logoUrl) && !logoFailed;
  // Com foto do CEO, o logo vira par do título — mesma linha, sem fundo.
  const inlineLogo = showImg && showLogo;

  const e = card.enrichment || {};
  const v = card.visual || {};
  const angle = ANGLE[e.angle];
  const body = e.consensus || e.historical;

  return (
    <article style={{
      background: C.card,
      backdropFilter: "blur(12px)",
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      overflow: "hidden",
      animation: "rr-week-hero-in 380ms ease-out both",
    }}>
      <Thumb
        card={card}
        imgUrl={imgUrl}
        logoUrl={logoUrl}
        showImg={showImg}
        showLogo={showLogo}
        onImgFail={() => setImgFailed(true)}
        onLogoFail={() => setLogoFailed(true)}
      />

      <div style={{ padding: "12px 14px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6,
                      marginBottom: 6, flexWrap: "wrap" }}>
          <ImpactBadge impact={card.impact} />
          {card.ticker && (
            <span style={{ fontFamily: "'IBM Plex Mono', monospace",
                           fontSize: 10.5, fontWeight: 500, color: C.purple,
                           letterSpacing: ".03em" }}>
              {tickerBase(card.ticker)}
            </span>
          )}
          {card.time_local && (
            <span style={{ fontFamily: "'IBM Plex Mono', monospace",
                           fontSize: 10.5, color: C.muted }}>
              {card.time_local}
            </span>
          )}
          {card.slot && SLOT_PT[card.slot] && (
            <span style={{ fontFamily: "'IBM Plex Mono', monospace",
                           fontSize: 9, color: C.dim, letterSpacing: ".05em" }}>
              {SLOT_PT[card.slot]}
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {inlineLogo && (
            <img
              src={logoUrl}
              alt=""
              onError={() => setLogoFailed(true)}
              style={{
                height: 34,
                maxWidth: 74,
                width: "auto",
                objectFit: "contain",
                flexShrink: 0,
                display: "block",
              }}
            />
          )}
          <h3 style={{ fontFamily: "Roble, Georgia, serif",
                       fontSize: 17, fontWeight: 700, lineHeight: 1.2,
                       color: C.text, margin: 0, letterSpacing: "-.005em",
                       minWidth: 0, flex: 1,
                       overflowWrap: "break-word" }}>
            {shortName(card)}
          </h3>
        </div>

        {v.person && (
          <div style={{ display: "flex", alignItems: "center", gap: 6,
                        marginTop: 6, overflow: "hidden" }}>
            <span style={{ width: 4, height: 4, borderRadius: "50%",
                           background: v.confidence === "high" ? C.purple : C.dim,
                           flexShrink: 0 }} />
            <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif",
                           fontSize: 12, fontWeight: 600, color: C.muted,
                           overflow: "hidden", textOverflow: "ellipsis",
                           whiteSpace: "nowrap" }}>
              {v.person}
            </span>
          </div>
        )}

        <DataStrip card={card} />

        {body && (
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 12.5, fontWeight: 500, lineHeight: 1.5,
                      color: C.text, margin: "9px 0 0", opacity: .88,
                      display: "-webkit-box", WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {body}
          </p>
        )}

        {angle && (
          <div style={{ marginTop: 8 }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace",
                           fontSize: 9.5, color: angle.color,
                           letterSpacing: ".05em" }}>
              ▸ {angle.label}
            </span>
          </div>
        )}
      </div>
    </article>
  );
}

function EventList({ cards, activeIdx, onSelect }) {
  const macroIdxs = [];
  const earningsIdxs = [];
  cards.forEach((c, i) => {
    if (c.kind === "earnings") earningsIdxs.push(i);
    else macroIdxs.push(i);
  });

  const renderItem = (i) => {
    const c = cards[i];
    const active = i === activeIdx;
    return (
      <button
        key={c.event_id}
        onClick={() => onSelect(i)}
        style={{
          display: "flex", alignItems: "center", gap: 8, width: "100%",
          textAlign: "left", padding: "5px 8px", borderRadius: 4,
          cursor: "pointer",
          background: active ? C.purpleDim : "transparent",
          border: "2px solid transparent",
          borderLeftColor: active ? C.purple : "transparent",
          color: "inherit",
          transition: "background .15s, border-color .15s",
        }}
      >
        <span style={{
          width: 5, height: 5, borderRadius: "50%",
          background: IMPACT[c.impact]?.color || C.dim,
          flexShrink: 0,
        }} />
        <span style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 12.5, fontWeight: active ? 700 : 500,
          color: active ? C.text : C.muted,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          flex: 1, minWidth: 0,
        }}>
          {shortName(c)}
        </span>
        {c.ticker && (
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9.5, color: active ? C.purple : C.dim,
            letterSpacing: ".03em", flexShrink: 0,
          }}>
            {tickerBase(c.ticker)}
          </span>
        )}
      </button>
    );
  };

  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
      {macroIdxs.map(renderItem)}
      {earningsIdxs.length > 0 && (
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9.5, fontWeight: 700,
          color: C.purple, letterSpacing: ".1em",
          margin: macroIdxs.length ? "12px 0 4px" : "0 0 4px",
          paddingLeft: 2,
        }}>
          RESULTADOS
        </div>
      )}
      {earningsIdxs.map(renderItem)}
    </div>
  );
}

function DayColumn({ day, imgBase, logoBase }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const cards = day.cards;
  const today = isToday(day.date);
  const [dd, mm] = day.date.split("-").reverse();

  useEffect(() => { setActiveIdx(0); }, [cards.length, day.date]);

  useEffect(() => {
    if (paused || cards.length <= 1) return;
    const t = setInterval(() => {
      setActiveIdx(i => (i + 1) % cards.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, cards.length]);

  const activeCard = cards[activeIdx] || cards[0];
  if (!activeCard) return null;

  return (
    <section
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{ minWidth: 0, display: "flex", flexDirection: "column" }}
    >
      <header style={{ paddingBottom: 9, marginBottom: 12,
                       borderBottom: `3px solid ${today ? C.purple : "#000"}` }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <h2 style={{ fontFamily: "Roble, Georgia, serif",
                       fontSize: 22, fontWeight: 800, letterSpacing: "-.01em",
                       color: today ? C.purple : C.text, margin: 0 }}>
            {day.weekday_pt}
          </h2>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace",
                         fontSize: 11, fontWeight: 500, color: C.muted,
                         letterSpacing: ".04em" }}>
            {dd}/{mm}
          </span>
          {today && (
            <span style={{ fontFamily: "'IBM Plex Mono', monospace",
                           fontSize: 9, color: C.purple, letterSpacing: ".08em",
                           border: `1px solid ${C.purpleEdge}`,
                           padding: "1px 5px", borderRadius: 3 }}>
              HOJE
            </span>
          )}
          <span style={{ marginLeft: "auto",
                         fontFamily: "'IBM Plex Mono', monospace",
                         fontSize: 10.5, color: C.dim }}>
            {activeIdx + 1}/{cards.length}
          </span>
        </div>
      </header>

      <HeroCard
        key={activeCard.event_id}
        card={activeCard}
        imgBase={imgBase}
        logoBase={logoBase}
      />

      <EventList
        cards={cards}
        activeIdx={activeIdx}
        onSelect={(i) => { setActiveIdx(i); setPaused(true); }}
      />
    </section>
  );
}

/* ── Componente principal ───────────────────────────────────────────────── */

export default function WeekAhead({
  src = "/api/week-ahead/latest",
  imgBase = "/static/week_ahead_images",
  logoBase = "http://100.119.212.17:8099",
  data: dataProp = null,
}) {
  const [data, setData] = useState(dataProp);
  const [loading, setLoading] = useState(!dataProp);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const isMobile = useIsMobile();

  useEffect(() => {
    if (dataProp) return;
    let alive = true;
    fetch(src)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => alive && (setData(j), setLoading(false)))
      .catch((err) => alive && (setError(err.message), setLoading(false)));
    return () => { alive = false; };
  }, [src, dataProp]);

  const days = useMemo(() => {
    if (!data?.days) return [];
    if (filter === "all") return data.days;
    return data.days
      .map((d) => ({ ...d, cards: d.cards.filter((c) => c.kind === filter) }))
      .filter((d) => d.cards.length);
  }, [data, filter]);

  if (loading) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 12, color: C.dim, letterSpacing: ".06em" }}>
        CARREGANDO AGENDA…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: "40px 20px", textAlign: "center",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 14, color: C.muted }}>
        Agenda indisponível no momento.
      </div>
    );
  }

  const s = data.summary || {};
  const colWidth = isMobile ? "min(88vw, 320px)" : "252px";

  return (
    <div style={{ background: C.bg, padding: "34px 22px 48px",
                  position: "relative", overflow: "hidden" }}>
      <style>{`
        @keyframes rr-week-hero-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .rr-week-scroll::-webkit-scrollbar { height: 8px; }
        .rr-week-scroll::-webkit-scrollbar-track { background: transparent; }
        .rr-week-scroll::-webkit-scrollbar-thumb {
          background: ${C.purpleEdge}; border-radius: 4px;
        }
        .rr-week-scroll::-webkit-scrollbar-thumb:hover {
          background: ${C.purple};
        }
      `}</style>

      <div aria-hidden style={{
        position: "absolute", top: -190, left: "50%",
        transform: "translateX(-50%)", width: 760, height: 360,
        background: `radial-gradient(ellipse, ${C.purpleDim} 0%, transparent 68%)`,
        pointerEvents: "none",
      }} />

      <div style={{ maxWidth: 1400, margin: "0 auto", position: "relative" }}>
        <header style={{ marginBottom: 26 }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 16,
                        flexWrap: "wrap", marginBottom: 6 }}>
            <h1 style={{ fontFamily: "Roble, Georgia, serif",
                         fontSize: isMobile ? 30 : 40, fontWeight: 800,
                         letterSpacing: "-.02em",
                         color: C.text, margin: 0, lineHeight: 1 }}>
              A Semana à Frente
            </h1>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace",
                           fontSize: 11, color: C.purple, letterSpacing: ".1em",
                           paddingBottom: 5 }}>
              {s.total} EVENTOS
            </span>
          </div>

          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 14, color: C.muted, margin: "0 0 16px",
                      maxWidth: 620, lineHeight: 1.5 }}>
            Resultados e indicadores que movem o mercado, com o consenso já
            precificado e o detalhe que decide a leitura.
          </p>

          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {[
              { k: "all",      label: "TUDO",        n: s.total },
              { k: "earnings", label: "RESULTADOS",  n: s.earnings },
              { k: "macro",    label: "INDICADORES", n: s.macro },
            ].map(({ k, label, n }) => {
              const on = filter === k;
              return (
                <button key={k} onClick={() => setFilter(k)}
                  style={{ fontFamily: "'IBM Plex Mono', monospace",
                           fontSize: 10.5, letterSpacing: ".07em",
                           color: on ? C.purple : C.muted,
                           background: on ? C.purpleDim : "transparent",
                           border: `1px solid ${on ? C.purpleEdge : C.border}`,
                           padding: "6px 12px", borderRadius: 4,
                           cursor: "pointer", transition: "all .18s" }}>
                  {label}
                  <span style={{ color: C.dim, marginLeft: 6 }}>{n}</span>
                </button>
              );
            })}
          </div>
        </header>

        <div
          className="rr-week-scroll"
          style={{
            display: "flex",
            gap: 18,
            overflowX: "auto",
            overflowY: "visible",
            scrollSnapType: "x mandatory",
            scrollBehavior: "smooth",
            paddingBottom: 14,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {days.map((day) => (
            <div key={day.date} style={{
              flex: "0 0 auto",
              width: colWidth,
              scrollSnapAlign: "start",
            }}>
              <DayColumn day={day} imgBase={imgBase} logoBase={logoBase} />
            </div>
          ))}
        </div>

        {!days.length && (
          <div style={{ padding: "50px 0", textAlign: "center",
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 14, color: C.dim }}>
            Nenhum evento nesse filtro.
          </div>
        )}

        <footer style={{ marginTop: 32, paddingTop: 16,
                         borderTop: `1px solid ${C.border}`,
                         display: "flex", gap: 16, flexWrap: "wrap",
                         fontFamily: "'IBM Plex Mono', monospace",
                         fontSize: 10, color: C.dim, letterSpacing: ".04em" }}>
          <span>{data.window?.start} — {data.window?.end}</span>
          <span style={{ marginLeft: "auto" }}>
            ATUALIZADO {new Date(data.generated_at).toLocaleString("pt-BR", {
              day: "2-digit", month: "2-digit",
              hour: "2-digit", minute: "2-digit",
            })}
          </span>
        </footer>
      </div>
    </div>
  );
}